import React from 'react';
import { motion } from 'framer-motion';
import { FaFileAlt, FaDownload, FaEye } from 'react-icons/fa';
import './TestArtifacts.css';

const TestArtifacts = () => {
  const artifacts = [
    {
      title: 'Test Plan',
      description: 'Comprehensive test planning document outlining scope, approach, resources, and schedule',
      icon: '📋',
      color: '#3b82f6',
      pages: '15-20',
      includes: ['Test Strategy', 'Scope & Objectives', 'Test Schedule', 'Resource Allocation', 'Risk Assessment']
    },
    {
      title: 'Test Strategy',
      description: 'High-level testing approach defining methodologies, tools, and quality standards',
      icon: '🎯',
      color: '#8b5cf6',
      pages: '10-15',
      includes: ['Testing Levels', 'Test Types', 'Entry/Exit Criteria', 'Defect Management', 'Automation Strategy']
    },
    {
      title: 'Test Scenarios',
      description: 'End-to-end user scenarios covering all critical business workflows',
      icon: '📝',
      color: '#10b981',
      pages: '20-30',
      includes: ['User Journeys', 'Business Workflows', 'Edge Cases', 'Negative Scenarios', 'Integration Points']
    },
    {
      title: 'Test Cases',
      description: 'Detailed test cases with step-by-step instructions and expected results',
      icon: '✅',
      color: '#f59e0b',
      pages: '50-100',
      includes: ['Test Steps', 'Test Data', 'Expected Results', 'Pre-conditions', 'Post-conditions']
    },
    {
      title: 'Bug Reports',
      description: 'Comprehensive defect documentation with severity, priority, and reproduction steps',
      icon: '🐛',
      color: '#ef4444',
      pages: '30-50',
      includes: ['Bug Description', 'Steps to Reproduce', 'Severity & Priority', 'Screenshots', 'Environment Details']
    },
    {
      title: 'RTM (Requirements Traceability Matrix)',
      description: 'Mapping between requirements, test cases, and defects for complete coverage',
      icon: '🔗',
      color: '#06b6d4',
      pages: '10-15',
      includes: ['Requirement IDs', 'Test Case Mapping', 'Coverage Analysis', 'Defect Linkage', 'Status Tracking']
    },
    {
      title: 'Test Summary Report',
      description: 'Executive summary of testing activities, results, and quality metrics',
      icon: '📊',
      color: '#ec4899',
      pages: '8-12',
      includes: ['Test Execution Summary', 'Defect Analysis', 'Test Coverage', 'Quality Metrics', 'Recommendations']
    },
    {
      title: 'Test Data',
      description: 'Structured test data sets for various testing scenarios and environments',
      icon: '💾',
      color: '#14b8a6',
      pages: '15-25',
      includes: ['Valid Data Sets', 'Invalid Data Sets', 'Boundary Values', 'Special Characters', 'SQL Scripts']
    }
  ];

  const handleDownload = (artifactTitle) => {
    alert(`Download functionality for "${artifactTitle}" will be available soon. Please contact for samples.`);
  };

  const handlePreview = (artifactTitle) => {
    alert(`Preview for "${artifactTitle}" will be available soon. Please contact for samples.`);
  };

  return (
    <section id="test-artifacts" className="test-artifacts">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">Test Artifacts</h2>
          <p className="section-subtitle">
            Professional QA documentation samples demonstrating industry best practices
          </p>
        </motion.div>

        <div className="artifacts-grid">
          {artifacts.map((artifact, index) => (
            <motion.div
              key={index}
              className="artifact-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -8 }}
            >
              <div className="artifact-header" style={{ backgroundColor: `${artifact.color}15` }}>
                <div className="artifact-icon" style={{ color: artifact.color }}>
                  {artifact.icon}
                </div>
                <h3 style={{ color: artifact.color }}>{artifact.title}</h3>
                <span className="artifact-pages">{artifact.pages} pages</span>
              </div>

              <div className="artifact-body">
                <p className="artifact-description">{artifact.description}</p>
                
                <div className="artifact-includes">
                  <h4>Includes:</h4>
                  <ul>
                    {artifact.includes.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="artifact-actions">
                <button 
                  className="artifact-btn preview"
                  onClick={() => handlePreview(artifact.title)}
                >
                  <FaEye /> Preview
                </button>
                <button 
                  className="artifact-btn download"
                  onClick={() => handleDownload(artifact.title)}
                >
                  <FaDownload /> Download
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="artifacts-note"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
        >
          <FaFileAlt />
          <p>
            All test artifacts follow industry standards and best practices. 
            Contact me for complete samples and templates.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default TestArtifacts;
